/**
 * Document Import hooks using React Query
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsService } from '@/services/documents';
import type {
  ConfirmAccountRequest,
  ResolveObservationRequest,
  UpdateObservationRequest,
  ImportTransactionsRequest,
  ObservationFilter,
  ApplyCategoryMappingsRequest,
  CompetitorApp,
  UploadInitRequest,
  UploadCompleteRequest,
  ListDocumentsParams,
  DocumentStatusFilter,
} from '@/types/document';

// Query keys for cache management
export const documentKeys = {
  all: ['documents'] as const,
  list: (params?: ListDocumentsParams) => [...documentKeys.all, 'list', params] as const,
  counts: () => [...documentKeys.all, 'counts'] as const,
  stagedImports: () => [...documentKeys.all, 'staged-imports'] as const,
  stagedImport: (id: string) => [...documentKeys.stagedImports(), id] as const,
  observations: (stagedImportId: string, filter?: ObservationFilter) =>
    [...documentKeys.stagedImport(stagedImportId), 'observations', filter] as const,
  document: (id: string) => [...documentKeys.all, 'document', id] as const,
  detectedCategories: (stagedImportId: string) =>
    [...documentKeys.stagedImport(stagedImportId), 'detected-categories'] as const,
  savedMappings: (sourceApp?: CompetitorApp) =>
    [...documentKeys.all, 'saved-mappings', sourceApp] as const,
};

// ============================================================================
// Document Upload
// ============================================================================

/**
 * Hook to upload a file and start its processing (upload/init, pre-signed POST,
 * upload/complete). Resolves with the document id to poll (WP-IMPORT-1).
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => documentsService.uploadFile(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

/**
 * Hook to initialize a pre-signed URL upload (Step 1 of 3)
 */
export function useInitUpload() {
  return useMutation({
    mutationFn: (request: UploadInitRequest) => documentsService.initUpload(request),
  });
}

/**
 * Hook to complete an upload (Step 3 of 3)
 */
export function useCompleteUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UploadCompleteRequest) => documentsService.completeUpload(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.stagedImports() });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

/**
 * Hook to get a document by ID
 */
export function useDocument(id: string) {
  return useQuery({
    queryKey: documentKeys.document(id),
    queryFn: () => documentsService.getDocument(id),
    enabled: !!id,
  });
}

/**
 * Convert UI status filter to API status values
 * Returns undefined for 'all' to fetch all documents
 * Returns array of statuses for 'pending_review' (pending_account + pending_review)
 */
export function mapStatusFilterToApiStatus(
  filter: DocumentStatusFilter
): string | undefined {
  switch (filter) {
    case 'all':
      return undefined;
    case 'pending_review':
      // Both pending_account and pending_review are shown as "Pending Review" in UI
      // The API should handle this, but we pass 'pending_review' which may need backend adjustment
      return 'pending_review';
    case 'processing':
      return 'processing';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    default:
      return undefined;
  }
}

/**
 * Hook to fetch list of documents with pagination and filtering
 */
export function useDocuments(params?: ListDocumentsParams) {
  return useQuery({
    queryKey: documentKeys.list(params),
    queryFn: async () => {
      try {
        return await documentsService.getDocuments(params);
      } catch (error) {
        // Return empty list for 404 (no documents endpoint may not exist yet)
        // or other expected "empty" states
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status === 404) {
            return {
              documents: [],
              total: 0,
              limit: params?.limit ?? 20,
              offset: params?.offset ?? 0,
            };
          }
        }
        throw error;
      }
    },
    // Keep previous data while fetching new data for smoother UX
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to delete a document
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => documentsService.deleteDocument(documentId),
    onSuccess: () => {
      // Invalidate all document list queries
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

/**
 * Hook to retry a failed document
 */
export function useRetryDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => documentsService.retryDocument(documentId),
    onSuccess: () => {
      // Invalidate all document queries to refresh the list
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

/**
 * Hook to fetch document counts by status for tab badges (WP-DOC-17)
 */
export function useDocumentCounts() {
  return useQuery({
    queryKey: documentKeys.counts(),
    queryFn: () => documentsService.getDocumentCounts(),
    staleTime: 30000, // 30 seconds - avoid excessive refetches
  });
}

// ============================================================================
// Staged Imports
// ============================================================================

/**
 * Hook to fetch a staged import by ID
 */
export function useStagedImport(id: string) {
  return useQuery({
    queryKey: documentKeys.stagedImport(id),
    queryFn: () => documentsService.getStagedImport(id),
    enabled: !!id,
  });
}

/**
 * Terminal document statuses where processing is complete
 * These statuses indicate the document pipeline has finished (successfully or not)
 */
const TERMINAL_DOCUMENT_STATUSES = ['staged', 'committed', 'failed', 'error', 'completed'] as const;

/**
 * Check if a document status is a terminal state (processing complete)
 */
function isTerminalStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return TERMINAL_DOCUMENT_STATUSES.includes(status as typeof TERMINAL_DOCUMENT_STATUSES[number]);
}

/**
 * Hook to poll for staged import by document ID (WP-BUG-19)
 * Used when document is still being processed and staged import is not yet available.
 * Polls every 2 seconds until staged import is found or document processing fails.
 *
 * Edge case handling (orphaned documents):
 * If a document reaches a terminal status (staged, committed, failed) but has no
 * staged_import_id, this indicates the document was processed before a bug fix
 * or only contains balance information. The hook stops polling and sets
 * isOrphanedDocument=true so the UI can show an appropriate error message.
 *
 * WP-BUG-67: the parse stage creates the staged import while the document is
 * still 'parsed' (deduplication and staging have not run yet), and the server
 * refuses a commit until the document is 'staged'. So the staged import is only
 * handed out once the document reaches a terminal status; until then the hook
 * keeps polling and reports isProcessing, even if the document already names a
 * staged import.
 */
export function useStagedImportByDocumentId(documentId: string | null) {
  // First, poll the document to check its status
  const documentQuery = useQuery({
    queryKey: documentKeys.document(documentId ?? ''),
    queryFn: () => documentsService.getDocument(documentId!),
    enabled: Boolean(documentId),
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling once the document reached a terminal status. A staged
      // import id alone is not enough (WP-BUG-67, see above). This also
      // handles orphaned documents that were processed before bug fixes.
      if (isTerminalStatus(data?.status)) return false;
      // Poll every 2 seconds while processing
      return 2000;
    },
  });

  // Once the document is ready, fetch its staged import
  const documentIsReady = isTerminalStatus(documentQuery.data?.status);
  const stagedImportId = documentIsReady ? documentQuery.data?.stagedImportId : undefined;
  const stagedImportQuery = useQuery({
    queryKey: documentKeys.stagedImport(stagedImportId ?? ''),
    queryFn: () => documentsService.getStagedImport(stagedImportId!),
    enabled: Boolean(stagedImportId),
  });

  // Detect orphaned documents: terminal status but no staged import
  // This can happen when:
  // 1. Document was processed before WP-BUG-20 fix was deployed
  // 2. Document only contains balance information (no transactions to import)
  const documentStatus = documentQuery.data?.status;
  const isOrphanedDocument = Boolean(
    documentId &&
    !stagedImportId &&
    isTerminalStatus(documentStatus) &&
    documentStatus !== 'failed' &&
    documentStatus !== 'error'
  );

  // Return combined state
  return {
    // The staged import, if available
    stagedImport: stagedImportQuery.data ?? null,
    stagedImportId: stagedImportId ?? null,
    // Document data for status display
    document: documentQuery.data ?? null,
    // Loading state - still processing if we have a documentId but no staged import yet
    // and document hasn't reached a terminal state
    isProcessing: Boolean(documentId) && !stagedImportId && !isTerminalStatus(documentStatus),
    // Orphaned document state - document finished but no staged import created
    isOrphanedDocument,
    // Error state
    isError: documentQuery.isError || stagedImportQuery.isError,
    error: documentQuery.error ?? stagedImportQuery.error,
    // Document status for UI display
    documentStatus: documentStatus ?? null,
    documentError: documentQuery.data?.errorMessage ?? null,
  };
}

/**
 * Hook to fetch all staged imports
 */
export function useStagedImports(params?: { status?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: [...documentKeys.stagedImports(), params],
    queryFn: () => documentsService.getStagedImports(params),
  });
}

/**
 * Hook to confirm account mapping
 */
export function useConfirmAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stagedImportId,
      request,
    }: {
      stagedImportId: string;
      request: ConfirmAccountRequest;
    }) => documentsService.confirmAccount(stagedImportId, request),
    onSuccess: (_data, { stagedImportId }) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.stagedImport(stagedImportId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.stagedImports() });
    },
  });
}

/**
 * Hook to resolve multiple accounts for a staged import (multi-account documents)
 */
export function useResolveAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stagedImportId,
      resolutions,
    }: {
      stagedImportId: string;
      resolutions: import('@/types/document').AccountResolution[];
    }) => documentsService.resolveAccounts(stagedImportId, resolutions),
    onSuccess: (_data, { stagedImportId }) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.stagedImport(stagedImportId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.stagedImports() });
      // Also invalidate accounts as new accounts may have been created
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// ============================================================================
// Transaction Observations
// ============================================================================

/**
 * Hook to fetch observations for a staged import
 */
export function useObservations(
  stagedImportId: string,
  params?: {
    filter?: ObservationFilter;
    limit?: number;
    offset?: number;
  }
) {
  return useQuery({
    queryKey: documentKeys.observations(stagedImportId, params?.filter),
    queryFn: () => documentsService.getObservations(stagedImportId, params),
    enabled: !!stagedImportId,
  });
}

/**
 * Hook to resolve an observation (duplicate handling)
 */
export function useResolveObservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      observationId,
      request,
    }: {
      observationId: string;
      request: ResolveObservationRequest;
      stagedImportId: string;
    }) => documentsService.resolveObservation(observationId, request),
    onSuccess: (_data, { stagedImportId }) => {
      // Invalidate all observation queries for this staged import
      queryClient.invalidateQueries({
        queryKey: documentKeys.stagedImport(stagedImportId),
      });
    },
  });
}

/**
 * Hook to update an observation
 */
export function useUpdateObservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      observationId,
      request,
    }: {
      observationId: string;
      request: UpdateObservationRequest;
      stagedImportId: string;
    }) => documentsService.updateObservation(observationId, request),
    onSuccess: (_data, { stagedImportId }) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.stagedImport(stagedImportId),
      });
    },
  });
}

/**
 * Hook to update observation category (WP-DOC-26)
 * Sets user override category or clears it (null reverts to LLM suggestion)
 */
export function useUpdateObservationCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stagedImportId,
      observationId,
      categoryId,
    }: {
      stagedImportId: string;
      observationId: string;
      categoryId: string | null;
    }) =>
      documentsService.updateObservationCategory(stagedImportId, observationId, {
        categoryId,
      }),
    onSuccess: (_data, { stagedImportId }) => {
      // Invalidate observations query to refresh the list
      queryClient.invalidateQueries({
        queryKey: documentKeys.stagedImport(stagedImportId),
      });
    },
  });
}

/**
 * Hook to bulk resolve observations
 */
export function useBulkResolveObservations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      observationIds,
      action,
    }: {
      observationIds: string[];
      action: 'import' | 'skip';
      stagedImportId: string;
    }) => documentsService.bulkResolveObservations(observationIds, action),
    onSuccess: (_data, { stagedImportId }) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.stagedImport(stagedImportId),
      });
    },
  });
}

// ============================================================================
// Import Operations
// ============================================================================

/**
 * Hook to import transactions from a staged import
 */
export function useImportTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stagedImportId,
      request,
    }: {
      stagedImportId: string;
      request: ImportTransactionsRequest;
    }) => documentsService.importTransactions(stagedImportId, request),
    onSuccess: (_data, { stagedImportId }) => {
      // Invalidate staged import and related queries
      queryClient.invalidateQueries({ queryKey: documentKeys.stagedImport(stagedImportId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.stagedImports() });
      // Also invalidate accounts and transactions as they may have changed
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to cancel a staged import
 */
export function useCancelStagedImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (stagedImportId: string) => documentsService.cancelStagedImport(stagedImportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.stagedImports() });
    },
  });
}

// ============================================================================
// Category Mapping (Competitor Import)
// ============================================================================

/**
 * Hook to fetch detected categories for a staged import
 */
export function useDetectedCategories(stagedImportId: string) {
  return useQuery({
    queryKey: documentKeys.detectedCategories(stagedImportId),
    queryFn: () => documentsService.getDetectedCategories(stagedImportId),
    enabled: !!stagedImportId,
  });
}

/**
 * Hook to apply category mappings to a staged import
 */
export function useApplyCategoryMappings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stagedImportId,
      request,
    }: {
      stagedImportId: string;
      request: ApplyCategoryMappingsRequest;
    }) => documentsService.applyCategoryMappings(stagedImportId, request),
    onSuccess: (_data, { stagedImportId, request }) => {
      // Invalidate detected categories and staged import
      queryClient.invalidateQueries({
        queryKey: documentKeys.detectedCategories(stagedImportId),
      });
      queryClient.invalidateQueries({
        queryKey: documentKeys.stagedImport(stagedImportId),
      });
      // If mappings were saved, invalidate saved mappings
      if (request.mappings.some((m) => m.saveForFuture)) {
        queryClient.invalidateQueries({
          queryKey: documentKeys.savedMappings(request.sourceApp),
        });
      }
    },
  });
}

/**
 * Hook to fetch saved category mappings
 */
export function useSavedMappings(sourceApp?: CompetitorApp) {
  return useQuery({
    queryKey: documentKeys.savedMappings(sourceApp),
    queryFn: () => documentsService.getSavedMappings(sourceApp),
  });
}

/**
 * Hook to delete a saved category mapping
 */
export function useDeleteSavedMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) => documentsService.deleteSavedMapping(mappingId),
    onSuccess: () => {
      // Invalidate all saved mappings queries
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.all, 'saved-mappings'],
      });
    },
  });
}
